import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignupLayout } from './Layout.tsx';
import { type AgeBand, useSignup } from './state.ts';

const AGE_BANDS: { value: AgeBand; label: string }[] = [
  { value: '5-7', label: '5 – 7  (Explorers)' },
  { value: '8-10', label: '8 – 10  (Builders)' },
  { value: '11-12', label: '11 – 12  (Designers)' },
];

/**
 * Screen 4 — child display name + age band.
 *
 * Spec §11.3.3:
 *   - Display name is what shows on screen during play; we nudge AGAINST
 *     real names ("Try a fun name like 'RocketDog' or 'Glitter Boss'")
 *     to limit identifier collection per privacy-compliance revision-3.
 *   - Age band is collected explicitly here even though §3 already has
 *     a tier picker — at MVP the tier picker UI doesn't exist yet, so
 *     this radio set is the source of truth and pre-selects Explorers.
 *   - Multi-child profiles deferred to Phase 4 — single child / account.
 */
export function Step4Child(): JSX.Element {
  const navigate = useNavigate();
  const { child, setChild } = useSignup();

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!child.displayName.trim()) return;
    navigate('/signup/5');
  };

  return (
    <SignupLayout
      step={4}
      title="What should we call your kid?"
      subtitle="A nickname or fun name works best — no real names needed."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <label className="text-sm text-slate-700" htmlFor="child-name">
          Display name
          <input
            id="child-name"
            type="text"
            required
            maxLength={20}
            placeholder="e.g. RocketDog"
            value={child.displayName}
            onChange={(e) => setChild({ displayName: e.target.value })}
            className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
          />
        </label>

        <fieldset>
          <legend className="text-sm font-medium text-slate-700">How old?</legend>
          <div className="mt-2 flex flex-col gap-2">
            {AGE_BANDS.map((band) => (
              <label
                key={band.value}
                htmlFor={`age-${band.value}`}
                className={`flex cursor-pointer items-center gap-3 rounded-2xl border-2 p-4 transition ${
                  child.ageBand === band.value
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <input
                  id={`age-${band.value}`}
                  type="radio"
                  name="ageBand"
                  value={band.value}
                  checked={child.ageBand === band.value}
                  onChange={() => setChild({ ageBand: band.value })}
                  className="h-5 w-5 cursor-pointer text-amber-500 focus:ring-amber-200"
                />
                <span className="text-sm text-slate-700">{band.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <button
          type="submit"
          disabled={!child.displayName.trim()}
          className="rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Continue
        </button>
      </form>
    </SignupLayout>
  );
}
